import React, { useState, useEffect } from "react";
import axios from "axios";
import { ArrowDown, ArrowUp, Clock, Briefcase, DollarSign, FileCheck } from "lucide-react";

export default function Dashboard() {
  const [jobsCount, setJobsCount] = useState(null);
  const [totalAmountSeeking, setTotalAmountSeeking] = useState(null);
  const [applications, setApplications] = useState(null);

  // Helper to download a file URL as a blob
  const downloadResume = async (url) => {
    try {
      const fullUrl = url.startsWith('http') ? url : `${process.env.REACT_APP_API_URL}${url.startsWith('/') ? url : '/' + url}`;
      const response = await axios.get(fullUrl, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      const fileName = fullUrl.split('/').pop() || 'resume.pdf';
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Failed to download resume', err);
      alert('Error downloading resume. Please try again.');
    }
  };

  // Fetch the user's jobs on mount
  useEffect(() => {
    async function fetchJobs() {
      try {
        const userId = localStorage.getItem("startupId");
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/jobs`);
        const userJobs = res.data.filter(job => String(job.userId) === String(userId));
        setJobsCount(userJobs.length);

        // Once jobs are fetched, fetch applications for each job
        const allApps = [];
        await Promise.all(
          userJobs.map(async job => {
            const appRes = await axios.get(
              `${process.env.REACT_APP_API_URL}/api/jobapplied/${job._id}`
            );
            allApps.push(...appRes.data);
          })
        );
        setApplications(allApps);
      } catch (err) {
        console.error("Failed to fetch jobs/applications", err);
      }
    }
    fetchJobs();
  }, []);

  // Fetch the user's fundings on mount and sum their amountSeeking
  useEffect(() => {
    async function fetchFundings() {
      try {
        const userId = localStorage.getItem("startupId");
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/fundings/me`, {
          params: { userId }
        });
        const total = res.data.reduce((sum, f) => {
          const num = parseFloat(String(f.amountSeeking).replace(/,/g, "")) || 0;
          return sum + num;
        }, 0);
        setTotalAmountSeeking(total);
      } catch (err) {
        console.error("Failed to fetch fundings", err);
      }
    }
    fetchFundings();
  }, []);

  // Inject dynamic values into the metrics array
  const metrics = [
    {
      title: "Jobs",
      value: jobsCount === null ? "Loading..." : jobsCount.toLocaleString(),
      trend: { value: "1.3% Up", direction: "up", period: "from past week" },
      icon: Briefcase,
      color: "bg-amber-50",
      iconColor: "text-amber-500",
      borderColor: "bg-green-500",
    },
    {
      title: "Funding Sought",
      value:
        totalAmountSeeking === null
          ? "Loading..."
          : `₹${totalAmountSeeking.toLocaleString("en-IN")}`,
      trend: { value: "4.3% Down", direction: "down", period: "from yesterday" },
      icon: DollarSign,
      color: "bg-green-50",
      iconColor: "text-green-500",
      borderColor: "bg-blue-500",
    },
    {
      title: "CA Pendings",
      value: "2040",
      trend: { value: "1.8% Up", direction: "up", period: "from yesterday" },
      icon: Clock,
      color: "bg-red-50",
      iconColor: "text-red-500",
      borderColor: "bg-purple-500",
    },
    {
      title: "CA Request",
      value: "40,689",
      trend: { value: "8.5% Up", direction: "up", period: "from yesterday" },
      icon: FileCheck,
      color: "bg-blue-50",
      iconColor: "text-blue-500",
      borderColor: "bg-orange-500",
    },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Dashboard</h1>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
        {metrics.map((metric, index) => {
          const IconComponent = metric.icon;
          return (
            <div key={index} className="bg-white rounded-lg shadow-sm relative overflow-hidden">
              <div className="p-4 md:p-6">
                <div className="flex justify-between items-start mb-3 md:mb-4">
                  <div>
                    <p className="text-gray-500 text-xs md:text-sm mb-1">{metric.title}</p>
                    <h3 className="text-xl md:text-2xl font-bold">{metric.value}</h3>
                  </div>
                  <div className={`p-2 md:p-3 rounded-full ${metric.color} flex-shrink-0`}>
                    <IconComponent className={`h-5 w-5 md:h-6 md:w-6 ${metric.iconColor}`} />
                  </div>
                </div>
                <div className="flex items-center text-xs">
                  {metric.trend.direction === "up" ? (
                    <ArrowUp className="h-3 w-3 text-green-500 mr-1 flex-shrink-0" />
                  ) : (
                    <ArrowDown className="h-3 w-3 text-red-500 mr-1 flex-shrink-0" />
                  )}
                  <span className={metric.trend.direction === "up" ? "text-green-500" : "text-red-500"}>
                    {metric.trend.value}
                  </span>
                  <span className="text-gray-500 ml-1 truncate">{metric.trend.period}</span>
                </div>
              </div>
              <div className={`h-1 w-full ${metric.borderColor} rounded-b-lg`}></div>
            </div>
          );
        })}
      </div>

      {/* Applications Table */}
      <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
        <h2 className="text-lg md:text-xl font-bold mb-4 md:mb-6">Application Received</h2>
        <div className="overflow-x-auto -mx-4 md:mx-0">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="py-2 md:py-3 px-2 md:px-4 text-left text-xs md:text-sm font-medium text-[#9f6b84] whitespace-nowrap">Name</th>
                  <th className="py-2 md:py-3 px-2 md:px-4 text-left text-xs md:text-sm font-medium text-[#9f6b84] whitespace-nowrap">Email</th>
                  <th className="py-2 md:py-3 px-2 md:px-4 text-left text-xs md:text-sm font-medium text-[#9f6b84] whitespace-nowrap">Phone</th>
                </tr>
              </thead>
              <tbody>
                {applications === null ? (
                  <tr><td colSpan={4} className="text-center py-4">Loading...</td></tr>
                ) : applications.length === 0 ? (
                  <tr><td colSpan={4} className="text-center py-4">No applications yet</td></tr>
                ) : (
                  applications.map((app) => (
                    <tr key={app._id} className="border-t">
                      <td className="py-2 px-2 md:px-4 whitespace-nowrap">{app.name}</td>
                      <td className="py-2 px-2 md:px-4 whitespace-nowrap">{app.email}</td>
                      <td className="py-2 px-2 md:px-4 whitespace-nowrap">{app.phone}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}